import type { FC } from 'react'
import { useState } from 'react'

import { Input, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import RefreshIcon from 'mdi-react/RefreshIcon'

import authResources from '@/api/auth'
import { useTranslation } from '@/components/i18n/hooks'
import { getNormalizedPhoneNumber } from '@/util/formatters/phoneNumbers'

import { Translation } from '../i18n/Translation'

const AccessCodeView: FC<{
  identifier: string
  countryId: number
}> = ({ identifier, countryId }) => {
  const translate = useTranslation()

  const [isGenerateCodeButtonDisabled, setIsGenerateCodeButtonDisabled] = useState(false)
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const generatePasswordMutation = useMutation(authResources.generatePassword)

  const handleGenerateCode = () => {
    generatePasswordMutation.mutate(
      { phoneNumber: getNormalizedPhoneNumber(identifier), countryId },
      {
        onSuccess() {
          setIsGenerateCodeButtonDisabled(true)

          setTimeout(() => {
            setIsGenerateCodeButtonDisabled(false)
          }, 5 * 1000)
        },
        onError() {
          setErrorPopupMessage({
            title: translate('Kodegenereringsfeil'),
            text: translate('Kunne ikke generere kode'),
          })
        },
      }
    )
  }

  const hasPasswordBeenGenerated = !generatePasswordMutation.isIdle
  const isIdentifierEmail = identifier.includes('@')

  return (
    <>
      <div className="send-code-button-container">
        <Button
          size="medium"
          className="mx-0 w-full"
          variant={hasPasswordBeenGenerated ? 'secondary' : 'primary'}
          Icon={hasPasswordBeenGenerated ? <RefreshIcon /> : undefined}
          disabled={isGenerateCodeButtonDisabled}
          isLoading={generatePasswordMutation.isLoading}
          onClick={handleGenerateCode}
        >
          {hasPasswordBeenGenerated ? (
            <Translation id="Send på nytt" />
          ) : isIdentifierEmail ? (
            <Translation id="Send kode på epost" />
          ) : (
            <Translation id="Send kode på SMS" />
          )}
        </Button>
      </div>

      <Field name="accessCode">
        {({ field, meta }: FieldProps) => (
          <Input
            isFullWidth
            capitalizeLabel
            id="accessCode"
            ariaLabel={translate('Kode')}
            marginBottom={16}
            error={meta.error}
            touched={meta.touched}
            placeholder={translate('Skriv inn kode')}
            {...field}
          />
        )}
      </Field>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />
    </>
  )
}

export default AccessCodeView
